import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from 'src/use-cases/user/userDetails';

/**
 * Hook to check if user has access to a settings page
 * Use this at the top of your component to prevent API calls for unauthorized users
 *
 * @param allowedRoles - Who can access this page:
 *   - 'systemAdmin': Only system admins (default for most settings)
 *   - 'creditViewer': System admins OR users with CREDIT_VIEWER role
 *   - 'both': Both system admins and credit viewers (same as creditViewer)
 * @returns object with hasAccess boolean and isLoading boolean
 */
export const useSettingsAccess = (allowedRoles: 'systemAdmin' | 'creditViewer' | 'both' = 'systemAdmin'): { hasAccess: boolean; isLoading: boolean } => {
  const { userDetails, isLoading } = useUserContext();
  const navigate = useNavigate();
  const userRoles = userDetails?.roles ?? [];
  const isCreditViewerRole = userRoles.includes('CREDIT_VIEWER');
  const isSystemAdmin = userDetails?.systemAdmin;

  // Check access based on allowed roles (only when not loading)
  const hasAccess = (() => {
    // While loading, assume no access yet
    if (isLoading) {
      return false;
    }

    switch (allowedRoles) {
      case 'systemAdmin':
        return isSystemAdmin;
      case 'creditViewer':
      case 'both':
        return isSystemAdmin || isCreditViewerRole;
      default:
        return false;
    }
  })();

  // Redirect to forbidden page if no access (only after loading completes)
  useEffect(() => {
    if (!isLoading && !hasAccess) {
      navigate('/forbidden', { replace: true });
    }
  }, [hasAccess, isLoading, navigate]);

  return { hasAccess, isLoading };
};
